import Seo from "@/components/site/Seo";
import { Link } from "react-router-dom";
import Reveal from "@/components/site/Reveal";
import { ArrowUpRight } from "lucide-react";

const practices = [
  {
    n: "01",
    tag: "Foundation",
    title: "Brand Building",
    body: "At the heart of what we do — we design the identity, the meaning and the memory that makes a brand instantly recognizable. Every touchpoint of your brand, engineered as a living system your team can scale for years.",
    items: [
      "Logo Design",
      "Brand Identity",
      "Motion Identity",
      "Typography",
      "Visual Identity",
      "Strategic Brand Thinking",
      "Premium Creative Direction",
    ],
    img: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=1400",
  },
  {
    n: "02",
    tag: "Everyday & moments",
    title: "Brand Experience",
    body: "We design the moments your brand meets people — the campaigns, the content, the creative that shows up across every channel, every business day. Consistency and craft, in the exact places your customers are actually looking.",
    items: [
      "Social Assets — LinkedIn, Instagram & Facebook, fully managed",
      "Ad Creatives — static and motion, across all platforms",
      "AI-Generated Imagery & Photography",
      "Product Launch Videos",
      "Motion Graphics",
      "Marketing Material & Email Templates",
    ],
    img: "https://images.unsplash.com/photo-1618556450991-2f1af64e8191?w=1400",
  },
  {
    n: "03",
    tag: "Product & web",
    title: "Digital Experience",
    body: "We design where your brand becomes something people use. Marketing sites, product interfaces, and design systems — engineered for conversion, retention and scale.",
    items: [
      "UI/UX Design",
      "Websites",
      "Design Systems",
      "Digital Products",
    ],
    img: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=1400",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo title="Creative Design Services — Brand, Campaigns, Digital | Midcreatives" description="Brand building, creatives and campaigns, brand experience, and digital experience — senior-talent-led design with creative direction on every piece." path="/services" />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="glow-blue" />
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pt-10 md:pt-16 pb-16 md:pb-24 relative">
          <p className="font-mono-mc mb-6">Services · Design capability</p>
          <h1
            data-testid="services-hero"
            className="font-display-tight text-5xl sm:text-7xl lg:text-[112px] ink leading-[0.92] max-w-6xl"
          >
            Design services <span className="ink-muted">for ambitious brands.</span>
          </h1>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8 max-w-6xl">
            <p className="md:col-span-7 text-lg md:text-xl ink-muted leading-relaxed">
              Teams at fast-growing companies use Midcreatives to get quality creative design done
              at scale — brand, campaigns, product and web, held under one senior team, all year.
            </p>
            <div className="md:col-span-5 flex flex-wrap items-start md:items-end md:justify-end gap-3">
              <Link to="/apply" data-testid="services-hero-cta" className="btn-dark">
                Get Membership <span className="arrow-slide">→</span>
              </Link>
              <Link to="/membership" className="btn-light">
                How it works
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PRACTICES — stacked editorial sections */}
      <section className="border-t border-white/10">
        {practices.map((p, i) => (
          <Reveal key={p.n} delay={0.05}>
            <article className="border-b border-white/10">
              <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-28">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center">
                  {/* Image */}
                  <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                    <div className="rounded-3xl overflow-hidden border border-white/10 aspect-[4/5]">
                      <img
                        src={p.img}
                        alt={p.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                    <p className="font-mono-mc mb-6">{p.n} · {p.tag}</p>
                    <h2 className="font-display-tight text-4xl md:text-6xl lg:text-7xl ink leading-[0.98]">
                      {p.title}
                    </h2>
                    <p className="mt-6 md:mt-8 ink-muted leading-relaxed text-base md:text-lg max-w-2xl">
                      {p.body}
                    </p>

                    <ul className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                      {p.items.map((it, idx) => (
                        <li
                          key={it}
                          className="border-t border-white/10 py-3.5 flex items-center justify-between gap-4 ink group cursor-default"
                        >
                          <span className="text-sm md:text-base">{it}</span>
                          <ArrowUpRight
                            size={14}
                            className="ink-muted group-hover:ink transition-colors shrink-0"
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </section>

      {/* HOW WE DELIVER — two-mode strip */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <p className="font-mono-mc mb-4">How we deliver</p>
            <h2 className="font-display-tight text-4xl md:text-6xl ink leading-[1.0]">
              Two modes. <span className="ink-muted">One senior team.</span>
            </h2>
          </div>
          <div className="md:col-span-7 flex md:items-end">
            <p className="ink-muted leading-relaxed">
              Daily creative served every business day. Big projects funded and ready when your
              moment comes. Same senior team all year — with creative direction on every piece.
            </p>
          </div>
        </div>

        <div className="border-t border-white/10">
          {[
            { n: "01", t: "Daily creative", d: "Ads, social, decks, banners, emails, landing pages — five hours a day, always on.", meta: "~100 hrs / month" },
            { n: "02", t: "Big projects", d: "Rebrands, launches, site rebuilds, category moves — held throughout the year.", meta: "Never expires" },
          ].map((m) => (
            <div key={m.n} className="grid grid-cols-12 gap-4 md:gap-8 py-8 md:py-10 border-b border-white/10 items-start">
              <span className="col-span-2 md:col-span-1 font-mono-mc pt-2">{m.n}</span>
              <div className="col-span-10 md:col-span-6">
                <h3 className="font-display-tight text-3xl md:text-4xl ink leading-tight">{m.t}</h3>
                <p className="ink-muted mt-3 leading-relaxed">{m.d}</p>
              </div>
              <div className="col-span-12 md:col-span-5 md:text-right pt-1">
                <span className="font-mono-mc ink">{m.meta}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* METHODOLOGY — editorial list */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <p className="font-mono-mc mb-4">Methodology</p>
            <h2 className="font-display-tight text-4xl md:text-6xl ink leading-[1.0]">
              Three <span className="ink-muted">non-negotiables.</span>
            </h2>
          </div>
        </div>

        <div className="border-t border-white/10">
          {[
            { n: "01", t: "Senior talent only", d: "No juniors learning on client brands. No rotating strangers. The team on your kickoff is the team on your last deliverable." },
            { n: "02", t: "Strategy before pixels", d: "Every engagement starts from positioning, audience and goal — never a brief translated into decoration." },
            { n: "03", t: "Built to compound", d: "Systems, identities and foundations over disposable one-offs. Every deliverable adds equity to the brand." },
          ].map((m) => (
            <div key={m.n} className="grid grid-cols-12 gap-4 md:gap-8 py-8 md:py-10 border-b border-white/10 items-start">
              <span className="col-span-2 md:col-span-1 font-mono-mc pt-2">{m.n}</span>
              <div className="col-span-10 md:col-span-4">
                <h3 className="font-display-tight text-2xl md:text-3xl ink leading-tight">{m.t}</h3>
              </div>
              <div className="col-span-12 md:col-span-7">
                <p className="ink-muted leading-relaxed">{m.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="rounded-[32px] border border-white/10 bg-[color:var(--surface)] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-end relative overflow-hidden">
          <div className="glow-blue" />
          <div className="md:col-span-8 relative">
            <h2 className="font-display-tight text-4xl md:text-6xl ink leading-[1.0]">
              Bring us the moment. <span className="ink-muted">We'll bring the craft.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right relative">
            <Link to="/apply" data-testid="services-apply-cta" className="btn-dark">
              Get Membership <span className="arrow-slide">→</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
